import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type BfsEnvironmentOption = 'training' | 'staging' | 'production';

export interface BfsConfigStatus {
  configured: boolean;
  dealershipConfigured?: boolean;
  environment?: BfsEnvironmentOption;
  environmentOverridden?: boolean;
}

interface FeatureFlagsResponse {
  feature_flags: Record<string, unknown>;
}

interface RawBfsConfig {
  dealershipId?: unknown;
  budcoProductId?: unknown;
}

const BFS_FINANCING_ENABLED_FLAG = 'BFS_FINANCING_ENABLED';
const BFS_DEALERSHIP_ID_FLAG = 'BFS_DEALERSHIP_ID';
const BFS_PRODUCT_MAPPINGS_FLAG = 'BFS_PRODUCT_MAPPINGS';

export interface BfsTenantFlags {
  enabled: boolean;
  dealershipId: string;
  productMappings: string;
}

@Injectable({ providedIn: 'root' })
export class BfsConfigWebService {
  private readonly http = inject(HttpClient);

  getConfigStatus() {
    return firstValueFrom(this.http.get<BfsConfigStatus>('/api/bfs/config'));
  }

  setEnvironment(environment: BfsEnvironmentOption) {
    return firstValueFrom(this.http.put<BfsConfigStatus>('/api/bfs/config/environment', { environment }));
  }

  useDefaultEnvironment() {
    return firstValueFrom(this.http.delete<BfsConfigStatus>('/api/bfs/config/environment'));
  }

  async getTenantFlags(): Promise<BfsTenantFlags> {
    const response = await firstValueFrom(this.http.get<FeatureFlagsResponse>('/api/feature-flags'));
    const feature_flags = response?.feature_flags ?? {};
    // Legacy tenants may still have dealer settings stored in the raw vsolv-config bag instead of feature flags.
    const rawBfsConfig = await this.getRawBfsConfig();

    return {
      enabled: Boolean(feature_flags[BFS_FINANCING_ENABLED_FLAG]),
      dealershipId:
        this.readFlagString(feature_flags[BFS_DEALERSHIP_ID_FLAG]) ??
        this.readFlagString(rawBfsConfig?.dealershipId) ??
        '',
      productMappings:
        this.readFlagString(feature_flags[BFS_PRODUCT_MAPPINGS_FLAG]) ??
        this.readFlagString(rawBfsConfig?.budcoProductId) ??
        '',
    };
  }

  setEnabled(enabled: boolean) {
    return firstValueFrom(
      this.http.post<FeatureFlagsResponse>('/api/feature-flags', {
        feature_flags: { [BFS_FINANCING_ENABLED_FLAG]: enabled },
      })
    );
  }

  setTenantSettings(dealershipId: string, productMappings: string) {
    return firstValueFrom(
      this.http.post<FeatureFlagsResponse>('/api/feature-flags', {
        feature_flags: {
          [BFS_DEALERSHIP_ID_FLAG]: dealershipId,
          [BFS_PRODUCT_MAPPINGS_FLAG]: productMappings,
        },
      })
    );
  }

  private readFlagString(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }

  private async getRawBfsConfig(): Promise<RawBfsConfig | null> {
    try {
      const config = await firstValueFrom(this.http.get<RawBfsConfig | null>('/api/vsolv-config/bfs_config'));
      return config ?? null;
    } catch {
      return null;
    }
  }
}
